/**
 * Invoice PTB comercial en Excel — misma composición que el PDF.
 */
import ExcelJS from 'exceljs';
import ptbImage from '../../imagenes/ptb.jpg';
import { usdAmountInWords } from './invoicePtcMerchandise';
import { resolvePtbTotalUsd, sumPtbLineTotals } from './invoicePtbTotal';
import { invoiceNumberForHeader } from './invoiceNumberUtils';
import { fmtNumBr, invoicePtbPdfFileName } from './generateInvoicePtbPDF';
import { invoiceIssueDateText } from './invoiceIssueDate';
import {
  BORDER,
  fmtDateLongEn,
  safe,
  setColWidths,
  styleRange,
  upper,
  workbookToDownload,
} from '../documentos/commercialExcelUtils';

const CNPJ_EMPRESA = '26.076.555/0001-00';
const COMPANY_LINES = [
  'PALM TRADE BRASIL IMPORTACAO E EXPORTACAO LTDA',
  'AVENIDA PREFEITO ALBERTO, NATALINO MIQUELUTE, 6777, SALA: 214; BOX: 04, ITINGA,',
  'ARAQUARI, SC, BRASIL, CEP: 89245000',
  'CNPJ-MF sob o nº: 26.076.555/0001-00',
];

const WHITE_FILL = {
  type: 'pattern',
  pattern: 'solid',
  fgColor: { argb: 'FFFFFFFF' },
};

const VERTICAL_BORDER = {
  left: BORDER.left,
  right: BORDER.right,
};

const DOCUMENT_COLUMN_OFFSET = 1;

async function imageUrlToDataUrl(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error('No se pudo cargar el logo de Invoice PTB.');
  const bytes = new Uint8Array(await response.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  return `data:image/jpeg;base64,${btoa(binary)}`;
}

function setMergedCell(ws, r1, c1, r2, c2, value, options = {}) {
  const shiftedC1 = c1 + DOCUMENT_COLUMN_OFFSET;
  const shiftedC2 = c2 + DOCUMENT_COLUMN_OFFSET;
  if (r1 !== r2 || shiftedC1 !== shiftedC2) ws.mergeCells(r1, shiftedC1, r2, shiftedC2);
  const cell = ws.getCell(r1, shiftedC1);
  cell.value = value || null;
  cell.font = {
    name: 'Arial',
    size: 8,
    color: { argb: 'FF000000' },
    ...(options.font || {}),
  };
  cell.alignment = {
    horizontal: 'center',
    vertical: 'middle',
    wrapText: true,
    ...(options.alignment || {}),
  };
  if (options.border) styleRange(ws, r1, shiftedC1, r2, shiftedC2, { border: options.border });
}

function applyOuterBorder(ws, r1, c1, r2, c2) {
  const shiftedC1 = c1 + DOCUMENT_COLUMN_OFFSET;
  const shiftedC2 = c2 + DOCUMENT_COLUMN_OFFSET;
  for (let row = r1; row <= r2; row += 1) {
    for (let col = shiftedC1; col <= shiftedC2; col += 1) {
      const cell = ws.getCell(row, col);
      cell.border = {
        ...cell.border,
        ...(row === r1 ? { top: BORDER.top } : {}),
        ...(row === r2 ? { bottom: BORDER.bottom } : {}),
        ...(col === shiftedC1 ? { left: BORDER.left } : {}),
        ...(col === shiftedC2 ? { right: BORDER.right } : {}),
      };
    }
  }
}

function setGridCells(ws, row, ranges, values, options = {}) {
  ranges.forEach(([from, to], index) => {
    setMergedCell(ws, row, from, row, to, values[index], {
      ...options,
      border: BORDER,
    });
  });
}

function portCityCountryForExcel(port, country) {
  const cleanPort = safe(port).replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
  const cleanCountry = safe(country).replace(/\s+/g, ' ').trim();
  return [cleanPort, cleanCountry].filter(Boolean).join(', ');
}

function totalDueWordsPtb(amount) {
  const n = Math.round(Number(amount) * 100) / 100;
  if (!Number.isFinite(n) || n <= 0) return '';
  const whole = Math.floor(n);
  const cents = Math.round((n - whole) * 100);
  const words = usdAmountInWords(whole)
    .replace(/\s+DOLLARS?$/i, '')
    .trim();
  return `${words}, ${String(cents).padStart(2, '0')}/100 USD DOLLARS`;
}

function addressLines(texto, paisDestino) {
  const country = upper(paisDestino);
  return safe(texto)
    .split(/\r?\n/)
    .map((ln) => ln.trim())
    .filter(Boolean)
    .filter((ln) => !/^PIN CODE\s*:/i.test(ln))
    .map((ln) => upper(ln))
    .filter((ln) => !country || ln !== country);
}

function priceCellPtb(v) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '';
  return fmtNumBr(v, 2);
}

function invoicePtbExcelFileName(params) {
  return invoicePtbPdfFileName(params).replace(/\.pdf$/i, '.xlsx');
}

export async function buildInvoicePtbExcelWorkbook(params) {
  const p = params || {};
  const wb = new ExcelJS.Workbook();
  wb.creator = 'PalmTrade ERP';
  const ws = wb.addWorksheet('Invoice PTB', {
    views: [{ showGridLines: false }],
    pageSetup: {
      orientation: 'portrait',
      paperSize: 9,
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: { left: 0.35, right: 0.35, top: 0.3, bottom: 0.35, header: 0, footer: 0 },
    },
  });
  setColWidths(ws, [3, 5, 5, 8, 8, 8, 8, 8, 8, 8, 8, 10, 10, 10]);

  const logoBase64 = await imageUrlToDataUrl(ptbImage);
  const logoId = wb.addImage({ base64: logoBase64, extension: 'jpeg' });
  ws.getRow(1).height = 10;
  ws.addImage(logoId, {
    tl: { col: 1, row: 1.15 },
    ext: { width: 128, height: 38 },
    editAs: 'oneCell',
  });

  [12, 12, 10, 10, 12].forEach((height, index) => {
    ws.getRow(index + 2).height = height;
  });
  setMergedCell(ws, 2, 11, 2, 13, `CNPJ ${CNPJ_EMPRESA}`, {
    font: { bold: true, size: 10 },
  });
  setMergedCell(ws, 3, 11, 4, 13, 'COMMERCIAL INVOICE', { font: { size: 8 } });
  setMergedCell(ws, 5, 11, 6, 13, `INVOICE N° ${invoiceNumberForHeader(p.numeroInvoice) || ''}`, {
    font: { bold: true, size: 11 },
  });
  applyOuterBorder(ws, 2, 11, 6, 13);

  let r = 5;
  for (const line of COMPANY_LINES) {
    setMergedCell(ws, r, 1, r, 9, line, {
      font: { bold: r === 7, size: 8 },
      alignment: { horizontal: 'left' },
    });
    ws.getRow(r).height = 10;
    r += 1;
  }

  r = 10;
  setMergedCell(
    ws,
    r,
    1,
    r,
    13,
    `FECHA DE EMISION / ISSUE DATE: ${p.issueCity || 'Santiago'}, ${invoiceIssueDateText(p.fechaInvoice, fmtDateLongEn)}`,
    { font: { size: 9 }, alignment: { horizontal: 'left' } }
  );
  ws.getRow(r).height = 14;
  r += 1;

  const addrs = addressLines(p.clienteDireccion, p.paisDestino);
  const customerStart = r;
  for (let row = customerStart; row < customerStart + 8; row += 1) {
    ws.getRow(row).height = 10;
  }
  setMergedCell(ws, r, 1, r, 4, 'Mrs./Señor(es):', { alignment: { horizontal: 'left' } });
  setMergedCell(ws, r, 5, r, 9, upper(p.clienteNombre), { alignment: { horizontal: 'left' } });
  setMergedCell(ws, r + 1, 1, r + 1, 4, 'Tax ID:', { alignment: { horizontal: 'left' } });
  setMergedCell(ws, r + 1, 5, r + 1, 9, upper(p.taxId), { alignment: { horizontal: 'left' } });
  setMergedCell(ws, r + 2, 1, r + 5, 4, 'Dirección/Address:', {
    alignment: { horizontal: 'left', vertical: 'top' },
  });
  setMergedCell(ws, r + 2, 5, r + 5, 9, addrs.join('\n'), {
    alignment: { horizontal: 'left', vertical: 'top' },
  });
  setMergedCell(ws, r + 6, 1, r + 6, 4, 'Phone:', { alignment: { horizontal: 'left' } });
  setMergedCell(ws, r + 6, 5, r + 6, 9, safe(p.clientePhone) || '22-2190119', {
    alignment: { horizontal: 'left' },
  });
  setMergedCell(ws, r + 7, 1, r + 7, 4, 'Fax:', { alignment: { horizontal: 'left' } });
  setMergedCell(ws, r + 7, 5, r + 7, 9, safe(p.clienteFax), {
    alignment: { horizontal: 'left' },
  });
  applyOuterBorder(ws, customerStart, 1, customerStart + 7, 9);

  setMergedCell(ws, r, 10, r + 1, 11, 'País de Origen\nOrigin Country', {
    border: BORDER,
    font: { size: 7 },
  });
  setMergedCell(ws, r, 12, r + 1, 13, 'País de Destino\nCountry Destination', {
    border: BORDER,
    font: { size: 7 },
  });
  setMergedCell(ws, r + 2, 10, r + 3, 11, upper(p.paisOrigen), { border: BORDER });
  setMergedCell(ws, r + 2, 12, r + 3, 13, upper(p.paisDestino), { border: BORDER });
  setMergedCell(ws, r + 4, 10, r + 5, 11, 'Puerto de Embarque\nLoading Port', {
    border: BORDER,
    font: { size: 7 },
  });
  setMergedCell(ws, r + 4, 12, r + 5, 13, 'Puerto de destino\nUnloading Port', {
    border: BORDER,
    font: { size: 7 },
  });
  setMergedCell(ws, r + 6, 10, r + 7, 11, upper(portCityCountryForExcel(p.puertoOrigen, p.paisOrigen)), {
    border: BORDER,
  });
  setMergedCell(ws, r + 6, 12, r + 7, 13, upper(portCityCountryForExcel(p.puertoDestino, p.paisDestino)), {
    border: BORDER,
  });
  r += 8;

  const vesselRanges = [[1, 5], [6, 9], [10, 11], [12, 13]];
  setGridCells(ws, r, vesselRanges, [
    'Buque\nVessel',
    'N° Contrato\nContract No.',
    'Orden de Compra\nPurchase Order',
    'N° Doc. Embarque\nBill of Lading',
  ], { font: { bold: true, size: 7 } });
  ws.getRow(r).height = 28;
  r += 1;
  setGridCells(ws, r, vesselRanges, [
    upper(p.nombreNave),
    upper(p.contractNo),
    '',
    upper(p.nombreBl),
  ]);
  ws.getRow(r).height = 22;
  r += 1;

  const salesRanges = [[1, 4], [5, 8], [9, 11]];
  setGridCells(ws, r, [...salesRanges, [12, 13]], [
    'Modalidad de la Venta\nTerms of Sale',
    'Cláusula de Venta\nClause',
    'Forma de Pago\nPayment Terms',
    'TOTAL KILOS\nTOTAL WEIGHT (kg)',
  ], { font: { bold: true, size: 7 } });
  ws.getRow(r).height = 28;
  r += 1;
  setMergedCell(ws, r, 1, r + 1, 4, upper(p.termsOfSale || p.salesTerms), { border: BORDER });
  setMergedCell(ws, r, 5, r + 1, 8, upper(p.clause || p.incoterms), { border: BORDER });
  setMergedCell(ws, r, 9, r + 1, 11, safe(p.paymentTerms), {
    border: BORDER,
    font: { size: 7 },
  });
  setGridCells(ws, r, [[12, 12], [13, 13]], ['Kgs. Netos\nNet Weight', 'Kgs. Brutos\nGross Weight'], {
    font: { bold: true, size: 7 },
  });
  r += 1;
  setGridCells(ws, r, [[12, 12], [13, 13]], [
    p.netWeight != null ? fmtNumBr(p.netWeight, 3) : '',
    p.grossWeight != null ? fmtNumBr(p.grossWeight, 3) : '',
  ]);
  r += 1;

  setMergedCell(
    ws,
    r,
    1,
    r,
    13,
    safe(p.remitPaymentText) || 'Please Remit Payment to    a: Palm Trade Brasil   Impo.   E    Expo. Ltda',
    { border: BORDER, font: { bold: true, size: 8 } }
  );
  ws.getRow(r).height = 18;
  r += 1;

  const merchRanges = [[1, 2], [3, 4], [5, 6], [7, 8], [9, 10], [11, 11], [12, 13]];
  const headR1 = r;
  setMergedCell(ws, r, 1, r, 2, 'Cantidad\nQuantity', {
    border: BORDER,
    font: { bold: true, size: 7 },
    alignment: { horizontal: 'left' },
  });
  setMergedCell(ws, r, 3, r, 10, 'DESCRIPCION MERCADERIA\nMERCHANDISE DESCRIPTION', {
    border: BORDER,
    font: { bold: true, size: 7 },
    alignment: { horizontal: 'left' },
  });
  setMergedCell(ws, r, 11, r, 11, 'UNIT PRICE\nUS $', {
    border: BORDER,
    font: { bold: true, size: 7 },
    alignment: { horizontal: 'right' },
  });
  setMergedCell(ws, r, 12, r, 13, 'VALOR TOTAL\nTOTAL VALUE\nUS $', {
    border: BORDER,
    font: { bold: true, size: 7 },
    alignment: { horizontal: 'right' },
  });
  ws.getRow(r).height = 30;
  r += 1;
  setGridCells(ws, r, merchRanges, [
    'M3',
    'Etiqueta',
    'Espessura\nmm',
    'Largura\nmm',
    'Comprimento\nmm',
    'us$/M3',
    '',
  ], { font: { bold: true, size: 7 }, alignment: { horizontal: 'left' } });
  ws.getRow(r).height = 25;
  ws.pageSetup.printTitlesRow = `${headR1}:${headR1 + 1}`;
  r += 1;

  const lineas = p.lineas || [];
  let merchandiseBodyHeight = 0;
  if (!lineas.length) {
    merchRanges.forEach(([from, to]) => {
      setMergedCell(ws, r, from, r, to, '', { border: VERTICAL_BORDER });
    });
    ws.getRow(r).height = 24;
    merchandiseBodyHeight += 24;
    r += 1;
  } else {
    for (const ln of lineas) {
      const rowIndex = r;
      const etiquetaDisplay = safe(ln.etiqueta).trim();
      const values = [
        ln.quantityM3 != null ? `${fmtNumBr(ln.quantityM3, 3)}  M3` : '',
        etiquetaDisplay,
        safe(ln.espessura),
        safe(ln.largura),
        safe(ln.comprimento),
        priceCellPtb(ln.unitPriceUsd),
        priceCellPtb(ln.totalValueUsd),
      ];
      merchRanges.forEach(([from, to], index) => {
        setMergedCell(ws, rowIndex, from, rowIndex, to, values[index], {
          border: VERTICAL_BORDER,
          alignment: {
            horizontal: index >= 5 ? 'right' : 'left',
            vertical: 'top',
            wrapText: true,
          },
        });
      });
      const rowHeight = Math.max(21, Math.ceil(safe(ln.etiqueta).length / 22) * 12);
      ws.getRow(rowIndex).height = rowHeight;
      merchandiseBodyHeight += rowHeight;
      r += 1;
    }
  }

  const merchandiseFillerHeight = Math.max(0, 240 - merchandiseBodyHeight);
  if (merchandiseFillerHeight > 3) {
    merchRanges.forEach(([from, to]) => {
      setMergedCell(ws, r, from, r, to, '', { border: VERTICAL_BORDER });
    });
    ws.getRow(r).height = merchandiseFillerHeight;
    r += 1;
  }

  const totalUsdNum =
    sumPtbLineTotals(lineas) ??
    (p.valorUsd != null && Number.isFinite(Number(p.valorUsd)) ? Number(p.valorUsd) : null) ??
    resolvePtbTotalUsd(lineas, null);

  const lineasTienenM3 = lineas.some(
    (ln) => ln.quantityM3 != null && ln.quantityM3 !== '' && Number.isFinite(Number(ln.quantityM3))
  );
  const totalM3Raw =
    p.quantity !== '' && p.quantity != null
      ? p.quantity
      : p.totalM3 != null
        ? p.totalM3
        : lineasTienenM3
          ? lineas.reduce((s, ln) => s + (Number(ln.quantityM3) || 0), 0)
          : null;
  const totalM3Fmt =
    totalM3Raw !== '' && totalM3Raw != null ? `${fmtNumBr(totalM3Raw, 3)} M3` : '';
  const totalUsdFmt = totalUsdNum != null ? fmtNumBr(totalUsdNum, 2) : '';

  setMergedCell(ws, r, 1, r, 10, `Total M3: ${totalM3Fmt}`, {
    border: BORDER,
    font: { bold: true, size: 8 },
    alignment: { horizontal: 'left' },
  });
  setMergedCell(ws, r, 11, r, 11, 'Total FOB USD', {
    border: BORDER,
    font: { bold: true, size: 8 },
    alignment: { horizontal: 'right' },
  });
  setMergedCell(ws, r, 12, r, 13, totalUsdFmt, {
    border: BORDER,
    font: { bold: true, size: 8 },
    alignment: { horizontal: 'right' },
  });
  r += 1;

  const dueWords = totalUsdNum != null ? totalDueWordsPtb(totalUsdNum) : '';
  setMergedCell(ws, r, 1, r, 13, {
    richText: [
      { font: { name: 'Arial', bold: true, size: 8 }, text: 'TOTAL A PAGAR\n' },
      { font: { name: 'Arial', size: 8 }, text: `TOTAL DUE: ${dueWords}\n` },
      { font: { name: 'Arial', size: 7 }, text: '( CON LETRA / WITH LETTERS )' },
    ],
  }, {
    border: BORDER,
    alignment: { horizontal: 'left', vertical: 'top', wrapText: true },
  });
  ws.getRow(r).height = 58;
  r += 2;

  r += 3;
  setMergedCell(ws, r, 10, r, 13, '', { border: { bottom: BORDER.bottom } });
  r += 1;
  setMergedCell(ws, r, 10, r, 13, 'FIRMA Y TIMBRE', { font: { size: 7 } });
  r += 1;
  setMergedCell(ws, r, 10, r, 13, '(SIGNATURE)', { font: { size: 7 } });
  r += 1;
  setMergedCell(ws, r, 9, r, 13, 'p.p. Palm Trade Brasil Impo. E Expo. Ltda.', {
    font: { size: 8, italic: true },
  });

  styleRange(ws, 1, 1, r, 14, {
    fill: WHITE_FILL,
    font: { name: 'Arial', color: { argb: 'FF000000' } },
  });
  ws.pageSetup.printArea = `A1:N${r}`;

  return wb;
}

export async function generateInvoicePtbExcel(params) {
  const wb = await buildInvoicePtbExcelWorkbook(params);
  await workbookToDownload(wb, invoicePtbExcelFileName(params));
}

export { invoicePtbExcelFileName };
